import { Building2, ScrollText } from 'lucide-react';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { SettingsMenu, type SettingsMenuEntry } from '../../../../components/settings/SettingsMenu';
import { PROCUREMENT_CATALOGS } from '../procurementCatalogTypes';

/*
  What Procurement Planning has to configure, and every row here is a vocabulary a PPMP LINE
  draws on — what is being bought, what kind of thing it is, and how it will be procured.

  THE TWO FLAT VOCABULARIES ARE DERIVED FROM THE DESCRIPTOR, never written out. They share the
  `procurement-catalogs.*` key set — maintaining the two is one administrative job — and the
  routes, the pages and these rows all read one array, so a third vocabulary added to it
  appears here without anybody remembering. A hand-kept list beside a descriptor is the second
  place a label drifts.

  PPMP ITEMS IS APPENDED LAST and is written out, because its row is a different shape: a
  rich-text specification with a unit and a cost, on its own module with its own keys. It is
  also the one a reader fills in over time rather than seeds once.
*/
const ENTRIES: SettingsMenuEntry[] = [
  ...PROCUREMENT_CATALOGS.map(
    (catalog): SettingsMenuEntry => ({
      key: catalog.slug,
      label: catalog.label,
      description: catalog.description,
      icon: catalog.icon,
      to: catalog.settingsPath,
      permission: 'procurement-catalogs.read',
    }),
  ),
  {
    key: 'ppmp-items',
    label: 'PPMP Items',
    description:
      'The specifications a procurement line is described with. A line may still describe anything — this is the list it can draw from in one pick.',
    icon: ScrollText,
    to: '/procurement/procurement-planning/settings/ppmp-items',
    permission: 'ppmp-items.read',
  },
  /*
    APPENDED LAST, and it is NOT a vocabulary a line draws on — it names WHICH OFFICES may file
    a PPMP at all. A row added to this menu goes after the ones a reader has already learned
    the position of, which is the rule every group in this product follows.
  */
  {
    key: 'ppmp-departments',
    label: 'PPMP Departments',
    description:
      'Which organizational units may file a PPMP. Every annual procurement plan lists one row per enrolled department, whether or not it has filed anything.',
    icon: Building2,
    to: '/procurement/procurement-planning/settings/ppmp-departments',
    permission: 'ppmp-departments.read',
  },
];

/**
 * Procurement → Procurement Planning → Settings: a menu of what the module group configures.
 *
 * An extension of the sidebar, which is what keeps the rail at one entry per module rather
 * than growing a row per configurable list.
 */
export function ProcurementPlanningSettingsPage() {
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-50">
      <Breadcrumb segments={['Procurement', 'Procurement Planning', 'Settings']} />

      <div className="modern-scrollbar min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto flex max-w-3xl flex-col gap-3">
          {/*
            One line, and it says what the menu cannot: every procurement line names a type and
            a mode, so both lists have to hold rows before one can be written.
          */}
          <p className="text-xs text-slate-500">
            The vocabularies a procurement line is written in — what is being bought, what kind
            of thing it is, and how it will be procured — and which offices may file one.
          </p>

          <SettingsMenu entries={ENTRIES} />
        </div>
      </div>
    </div>
  );
}
